<template>
  <div class="px-4 pb-4">
    <!-- Match /assets (EquipmentPage): header -->
    <PageHeaderSearch>
      <template #search>
        <IconField>
          <InputIcon class="pi pi-search" />
          <InputText
            id="wiki-search"
            v-model="searchTerm"
            type="search"
            class="pl-4 w-64"
            :disabled="isLoading"
            placeholder="Search assets..."
          />
        </IconField>
        <Button
          label="Available"
          :class="{ 'p-button-secondary': showAvailableOnly }"
          :disabled="isLoading"
          severity="secondary"
          raised
          @click="toggleAvailable"
        />
      </template>
      <template #actions>
        <div class="flex flex-wrap items-center gap-2">
          <Button
            v-if="canWikiImport"
            :label="importBasketCount ? `Import (${importBasketCount})` : 'Import'"
            icon="pi pi-download"
            severity="secondary"
            size="small"
            :disabled="isLoading"
            @click="goToImport"
          />
          <Button
            label="Add Asset"
            icon="pi pi-plus"
            severity="primary"
            size="small"
            :disabled="isLoading"
            @click="goToAddAsset"
          />
        </div>
      </template>
    </PageHeaderSearch>

    <!-- Filters: same card as /assets -->
    <div class="bg-white rounded-lg shadow p-4 mb-4">
      <div class="flex flex-wrap items-center justify-between gap-4">
        <div class="flex flex-wrap gap-4 items-center">
          <ClassificationDropdowns
            wiki-catalog-options
            :initial-department="selectedDepartment"
            :initial-master-category="selectedMasterCategory"
            :initial-category="selectedCategory"
            @change="onClassificationChange"
          />
        </div>
        <Button
          label="Clear Filters"
          icon="pi pi-times"
          :disabled="!hasActiveFilters"
          severity="secondary"
          raised
          @click="clearAllFilters"
        />
      </div>

      <div
        v-if="hasActiveFilters"
        class="mt-4 pt-4 border-t border-gray-200"
      >
        <div class="flex items-center gap-2 text-sm text-gray-600">
          <span class="font-medium">Active Filters:</span>
          <Chip
            v-if="selectedDepartment"
            :label="getDepartmentName(selectedDepartment)"
            icon="pi pi-building"
            removable
            @remove="clearDepartment"
          />
          <Chip
            v-if="selectedMasterCategory"
            :label="getMasterCategoryName(selectedMasterCategory)"
            icon="pi pi-sitemap"
            removable
            @remove="clearMasterCategory"
          />
          <Chip
            v-if="selectedCategory"
            :label="getCategoryName(selectedCategory)"
            icon="pi pi-tag"
            removable
            @remove="clearCategory"
          />
          <Chip
            v-if="searchTerm"
            :label="`Search: ${searchTerm}`"
            icon="pi pi-search"
            removable
            @remove="clearSearch"
          />
        </div>
      </div>
    </div>

    <WikiCatalog
      ref="wikiCatalogRef"
      :external-search-term="searchTerm"
      :filters="wikiFilters"
    />
  </div>
</template>

<script setup>
import WikiCatalog from '@/components/wiki/WikiComponent.vue';
import { ref, watch, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';

import { getWikiCatalogFilterOptions } from '@/services/projectService';
import ClassificationDropdowns from '@/components/shared/ClassificationDropdowns.vue';
import PageHeaderSearch from '@/shared/components/PageHeaderSearch.vue';
import { useAuthStore } from '@/stores/authStore';
import { useWikiImportBasketStore } from '@/stores/wikiImportBasketStore';

const route = useRoute();
const router = useRouter();
const wikiCatalogRef = ref(null);
const authStore = useAuthStore();
const wikiImportBasket = useWikiImportBasketStore();
const { count: importBasketCount } = storeToRefs(wikiImportBasket);

const canWikiImport = computed(
    () => authStore.isCompanyAdmin || authStore.isSystemAdmin
);

const showAvailableOnly = ref(false);
const isLoading = computed(() => wikiCatalogRef.value?.isLoading ?? false);

function toggleAvailable() {
    showAvailableOnly.value = !showAvailableOnly.value;
}

function goToAddAsset() {
    router.push('/assets');
}

function goToImport() {
    router.push('/wiki/import');
}

/** Read an integer from a URL query param, or null. */
function intFromQuery(val) {
  if (val == null || val === '') return null;
  const n = parseInt(String(val), 10);
  return Number.isFinite(n) ? n : null;
}

// ── Initialise synchronously from URL so the first WikiComponent fetch is already filtered ──
const selectedDepartment = ref(intFromQuery(route.query.department));
const selectedMasterCategory = ref(intFromQuery(route.query.masterCategory));
const selectedCategory = ref(intFromQuery(route.query.category));
const searchTerm = ref(
  route.query.search != null && String(route.query.search).trim() !== ''
    ? String(route.query.search)
    : ''
);

const wikiFilters = ref((() => {
  const f = {};
  if (selectedDepartment.value) f.department_id = selectedDepartment.value;
  if (selectedMasterCategory.value) f.master_category_id = selectedMasterCategory.value;
  if (selectedCategory.value) f.category_id = selectedCategory.value;
  return f;
})());

const departments = ref([]);
const masterCategories = ref([]);
const categories = ref([]);

const filterOptionsCache = ref(null);
const lastCacheRefresh = ref(null);
const CACHE_DURATION = 5 * 60 * 1000;
const FILTER_STORAGE_KEY = 'wiki_catalog_filters';

const updateURL = () => {
  const query = {};
  if (selectedDepartment.value) query.department = selectedDepartment.value;
  if (selectedMasterCategory.value) query.masterCategory = selectedMasterCategory.value;
  if (selectedCategory.value) query.category = selectedCategory.value;
  if (searchTerm.value) query.search = searchTerm.value;
  if (Object.keys(query).length > 0 || Object.keys(route.query).length > 0) {
    router.replace({ query });
  }
};

const saveFiltersToStorage = () => {
  try {
    localStorage.setItem(
      FILTER_STORAGE_KEY,
      JSON.stringify({
        department: selectedDepartment.value,
        masterCategory: selectedMasterCategory.value,
        category: selectedCategory.value,
        search: searchTerm.value,
      })
    );
  } catch (e) {
    console.warn('Failed to save wiki filters', e);
  }
};

const loadFiltersFromStorage = () => {
  try {
    const stored = localStorage.getItem(FILTER_STORAGE_KEY);
    if (stored) {
      const f = JSON.parse(stored);
      selectedDepartment.value = f.department || null;
      selectedMasterCategory.value = f.masterCategory || null;
      selectedCategory.value = f.category || null;
      searchTerm.value = f.search || '';
      updateWikiFilters();
    }
  } catch (e) {
    console.warn('Failed to load wiki filters', e);
  }
};

function updateWikiFilters() {
  const filters = {};
  if (selectedDepartment.value) filters.department_id = selectedDepartment.value;
  if (selectedMasterCategory.value) filters.master_category_id = selectedMasterCategory.value;
  if (selectedCategory.value) filters.category_id = selectedCategory.value;
  wikiFilters.value = filters;
  updateURL();
  saveFiltersToStorage();
}

/** Re-sync filter state from the URL (route navigation, back/forward). */
const loadFiltersFromURL = () => {
  const query = route.query;
  selectedDepartment.value = intFromQuery(query.department);
  selectedMasterCategory.value = intFromQuery(query.masterCategory);
  selectedCategory.value = intFromQuery(query.category);
  searchTerm.value =
    query.search != null && String(query.search).trim() !== '' ? String(query.search) : '';
  updateWikiFilters();
};

// Only re-sync when the URL actually changes (e.g. browser back/forward, external navigation).
// Guard against our own router.replace calls by checking whether the values actually differ.
watch(
  () => route.query,
  (q) => {
    const d = intFromQuery(q.department);
    const m = intFromQuery(q.masterCategory);
    const c = intFromQuery(q.category);
    const s = q.search != null && String(q.search).trim() !== '' ? String(q.search) : '';
    if (
      d !== selectedDepartment.value ||
      m !== selectedMasterCategory.value ||
      c !== selectedCategory.value ||
      s !== searchTerm.value
    ) {
      loadFiltersFromURL();
    }
  },
  { deep: true }
);

onMounted(() => {
  wikiImportBasket.loadFromStorage();
  // Load dropdown display data without blocking the initial (already-filtered) fetch.
  loadAllFilterOptions();
  // If no URL params, restore the last session filters from localStorage.
  if (Object.keys(route.query).length === 0) {
    loadFiltersFromStorage();
  }
});

const loadAllFilterOptions = async (forceRefresh = false) => {
  try {
    const now = Date.now();
    if (
      !forceRefresh &&
      filterOptionsCache.value &&
      lastCacheRefresh.value &&
      now - lastCacheRefresh.value < CACHE_DURATION
    ) {
      departments.value = filterOptionsCache.value.departments || [];
      masterCategories.value = filterOptionsCache.value.masterCategories || [];
      categories.value = filterOptionsCache.value.categories || [];
      return;
    }
    const filterOptions = await getWikiCatalogFilterOptions();
    filterOptionsCache.value = filterOptions;
    lastCacheRefresh.value = now;
    departments.value = filterOptions.departments || [];
    masterCategories.value = filterOptions.masterCategories || [];
    categories.value = filterOptions.categories || [];
  } catch (e) {
    console.error('[WikiPage] filter options', e);
    if (!filterOptionsCache.value) {
      departments.value = [];
      masterCategories.value = [];
      categories.value = [];
    }
  }
};

const onClassificationChange = (classification) => {
  selectedDepartment.value = classification.department;
  selectedMasterCategory.value = classification.masterCategory;
  selectedCategory.value = classification.category;
  updateWikiFilters();
};

const clearDepartment = () => {
  selectedDepartment.value = null;
  selectedMasterCategory.value = null;
  selectedCategory.value = null;
  masterCategories.value = [];
  categories.value = [];
  updateWikiFilters();
};

const clearMasterCategory = () => {
  selectedMasterCategory.value = null;
  selectedCategory.value = null;
  updateWikiFilters();
};

const clearCategory = () => {
  selectedCategory.value = null;
  updateWikiFilters();
};

const clearSearch = () => {
  searchTerm.value = '';
  updateWikiFilters();
};

const clearAllFilters = () => {
  selectedDepartment.value = null;
  selectedMasterCategory.value = null;
  selectedCategory.value = null;
  searchTerm.value = '';
  updateWikiFilters();
};

const getDepartmentName = (id) => {
  const n = Number(id);
  const dept = departments.value.find((d) => Number(d.id) === n);
  return dept ? dept.name : 'Unknown Department';
};

const getMasterCategoryName = (id) => {
  const n = Number(id);
  const mc = masterCategories.value.find((m) => Number(m.id) === n);
  return mc ? mc.name : 'Unknown Master Category';
};

const getCategoryName = (id) => {
  const n = Number(id);
  const cat = categories.value.find((c) => Number(c.id) === n);
  return cat ? cat.name : 'Unknown Category';
};

const hasActiveFilters = computed(
  () =>
    selectedDepartment.value ||
    selectedMasterCategory.value ||
    selectedCategory.value ||
    searchTerm.value
);

watch(searchTerm, () => {
  updateURL();
  saveFiltersToStorage();
});
</script>
